import * as React from 'react';

import { useAtomValue } from 'jotai';

import type { UStudioConnectionStatus, UStudioContextValue } from './types';
import { UStudioContext } from './UStudioContext';

import {
  connectDevice,
  D200Agent,
  disconnectDevice,
  getDevices,
  getLayout,
  requestDevice,
  type AgentInfo,
} from '~/shared/lib/ustudio';
import { $user } from '~/shared/providers/auth/identity.localState';

async function renderInitialLayout(agent: D200Agent): Promise<void> {
  const layout = getLayout();
  const btn13 = layout.buttons[13];

  await agent.clear();

  const response = await fetch('/icon.png');
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);

  const canvas = new OffscreenCanvas(btn13.width, btn13.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, btn13.width, btn13.height);

  const scale = Math.min(
    btn13.width / bitmap.width,
    btn13.height / bitmap.height,
  );
  const drawW = Math.round(bitmap.width * scale);
  const drawH = Math.round(bitmap.height * scale);
  const drawX = Math.round((btn13.width - drawW) / 2);
  const drawY = Math.round((btn13.height - drawH) / 2);
  ctx.drawImage(bitmap, drawX, drawY, drawW, drawH);
  bitmap.close();

  const pngBlob = await canvas.convertToBlob({ type: 'image/png' });
  const pngData = await pngBlob.arrayBuffer();
  await agent.drawButton(13, pngData);
}

const SESSION_KEY = 'd200:skipped';

export const UStudioProvider = ({ children }: React.PropsWithChildren) => {
  const user = useAtomValue($user);

  const [status, setStatus] =
    React.useState<UStudioConnectionStatus>('disconnected');
  const [agent, setAgent] = React.useState<D200Agent | null>(null);
  const [deviceInfo, setDeviceInfo] = React.useState<AgentInfo | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const agentRef = React.useRef<D200Agent | null>(null);
  const deviceRef = React.useRef<HIDDevice | null>(null);

  const connectWithDevice = React.useCallback(
    async (mode: 'silent' | 'manual') => {
      setStatus('connecting');
      setError(null);

      try {
        const device =
          mode === 'silent' ? (await getDevices())[0] : await requestDevice();

        if (!device) {
          setStatus('disconnected');
          return;
        }

        const connectedDevice = await connectDevice(device);
        deviceRef.current = connectedDevice;

        const newAgent = new D200Agent(connectedDevice);
        const info = await newAgent.start(
          () => undefined,
          (msg) => setError(msg),
        );

        agentRef.current = newAgent;
        setAgent(newAgent);
        setDeviceInfo(info);
        setStatus('connected');

        renderInitialLayout(newAgent).catch(() => undefined);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [],
  );

  const connect = React.useCallback(async () => {
    await connectWithDevice('manual');
  }, [connectWithDevice]);

  const disconnect = React.useCallback(async () => {
    agentRef.current?.stop();
    agentRef.current = null;
    setAgent(null);
    setDeviceInfo(null);

    if (deviceRef.current) {
      await disconnectDevice(deviceRef.current).catch(() => undefined);
      deviceRef.current = null;
    }

    setStatus('disconnected');
    setError(null);
  }, []);

  React.useEffect(() => {
    if (!user?.id) return;
    if (!('hid' in navigator)) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    connectWithDevice('silent').catch(() => {
      sessionStorage.setItem(SESSION_KEY, 'true');
    });
  }, [user?.id, connectWithDevice]);

  React.useEffect(
    () => () => {
      agentRef.current?.stop();
      if (deviceRef.current) {
        disconnectDevice(deviceRef.current).catch(() => undefined);
      }
    },
    [],
  );

  const value = React.useMemo<UStudioContextValue>(
    () => ({
      status,
      agent,
      deviceInfo,
      error,
      connect,
      disconnect,
    }),
    [status, agent, deviceInfo, error, connect, disconnect],
  );

  return (
    <UStudioContext.Provider value={value}>{children}</UStudioContext.Provider>
  );
};
